import styled from '@emotion/styled'
import { HandMainKey, TCards } from '@lbs/shared-rp-types'
import { HAND_COMBINATIONS_LABELS } from '@lbs/shared-rp-types/dist/payout'
import { Event as EventIcon, PlayCircle as PlayIcon, StopCircle as StopIcon } from '@mui/icons-material'
import { Box, Chip, Divider, List, ListItem, ListItemIcon, ListItemText, Paper, Stack, Typography } from '@mui/material'
import { FilterBar } from 'components'
import { queryKeys } from 'constants/EQueryKeysConstant'
import mrtLocalization from 'constants/mrtLocalization'
import { formatDuration, intervalToDuration } from 'date-fns'
import { ru } from 'date-fns/locale'
import { MaterialReactTable, MRT_ColumnDef, MRT_PaginationState, useMaterialReactTable } from 'material-react-table'
import { observer } from 'mobx-react-lite'
import { Prp2boxApiGames } from 'prp2boxApi'
import { IRpSession } from 'prp2boxApi/games'
import { FC, useState } from 'react'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import routes from 'router/routes'
import { formatToMoscowTZ } from 'utils/date'
import { CardPic } from '../RpGameSessionDetails'

const CombinationName = styled.div``
const CardPicWrapper = styled.div`
  display: flex;
  position: relative;
`

const BoxSeatNumber = styled.div`
  position: absolute;
  top: 3px;
  right: 3px;
  border-radius: 50%;
  border: 1px solid #ccc;
  width: 20px;
  height: 20px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #ccc;
`

const columns: MRT_ColumnDef<IRpSession>[] = [
  {
    accessorKey: 'id',
    header: 'ID',
    enableSorting: false,
    Cell: ({ cell, row }) => {
      return (
        <>
          <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
            ID: #{cell.getValue<number>()}
          </Typography>
          <Divider />
          <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
            Public ID: #{row.original.publicId}
          </Typography>
        </>
      )
    },
  },
  {
    accessorKey: 'dealer',
    enableSorting: false,
    header: 'Дилер',
    Cell: ({ cell }) => {
      const dealer = cell.getValue<{ cards: string[]; combination: HandMainKey }>()
      return (
        <div>
          <CombinationName>{HAND_COMBINATIONS_LABELS.ru[dealer.combination] || '—'}</CombinationName>
          <CardPicWrapper>
            {dealer.cards.map((card) => (
              <CardPic
                key={card}
                value={card as TCards}
                alt={card}
                className="card-pic"
                style={{ width: 22, height: 35 }}
              />
            ))}
          </CardPicWrapper>
        </div>
      )
    },
  },
  {
    accessorKey: 'boxes',
    enableSorting: false,
    header: 'Боксы',
    Cell: ({ cell }) => {
      const boxes = cell.getValue<
        Array<{
          seatNumber: number
          combination: HandMainKey
          cards: string[]
          extraCombination?: HandMainKey
        }>
      >()

      if (boxes.length === 0) {
        return <Typography variant="body2">Нет боксов</Typography>
      }

      return (
        <div>
          {boxes.map((box) => (
            <Paper key={box.seatNumber} sx={{ p: 0.5, mb: 1, position: 'relative' }}>
              <BoxSeatNumber>{box.seatNumber}</BoxSeatNumber>
              <CombinationName>{HAND_COMBINATIONS_LABELS.ru[box.combination] || '—'}</CombinationName>
              {box?.extraCombination && (
                <CombinationName>+ {HAND_COMBINATIONS_LABELS.ru[box?.extraCombination]}</CombinationName>
              )}
              <CardPicWrapper>
                {box.cards.map((card) => (
                  <CardPic
                    key={card}
                    value={card as TCards}
                    alt={card}
                    className="card-pic"
                    style={{ width: 22, height: 35 }}
                  />
                ))}
              </CardPicWrapper>
            </Paper>
          ))}
        </div>
      )
    },
  },
  {
    accessorKey: 'sessionNet',
    enableSorting: false,
    header: 'Прибыль',
    Cell: ({ cell }) => {
      const value = cell.getValue<number>()

      if (value === null || value === undefined) {
        return (
          <Typography variant="body2" color="text.secondary">
            —
          </Typography>
        )
      }

      const isPositive = value > 0
      const isNegative = value < 0

      let color: string
      if (isPositive) {
        color = 'success.main'
      } else if (isNegative) {
        color = 'error.main'
      } else {
        color = 'text.primary'
      }

      return (
        <Typography variant="body2" fontWeight="medium" color={color} sx={{ fontFamily: 'monospace' }}>
          {isPositive && '+'}
          {value}
        </Typography>
      )
    },
  },
  {
    accessorKey: 'startedAt',
    header: 'Начало',
    sortingFn: 'datetime',
    enableSorting: false,
    Cell: ({ cell }) => {
      return formatToMoscowTZ(cell.getValue<string>())
    },
  },
  {
    accessorKey: 'endedAt',
    header: 'Конец',
    sortingFn: 'datetime',
    enableSorting: false,
    Cell: ({ cell }) => {
      return formatToMoscowTZ(cell.getValue<string>())
    },
  },
  {
    accessorKey: 'durationSec',
    header: 'Длительность',
    enableSorting: false,
    Cell: ({ cell }) => {
      const seconds = cell.getValue<number>()
      if (!seconds) return '—'

      const duration = intervalToDuration({ start: 0, end: seconds * 1000 })
      return formatDuration(duration, {
        format: ['hours', 'minutes', 'seconds'],
        locale: ru,
        delimiter: ', ',
      })
    },
  },
  {
    accessorKey: 'phase',
    enableSorting: false,
    header: 'Статус',
  },
]

const RpGameSessions: FC = observer(() => {
  const params = useParams<{ gameId: string }>()
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 10 })

  if (!params.gameId || Number.isNaN(Number(params.gameId))) {
    return <Typography variant="h6">Игра не найдена</Typography>
  }

  const gameId = Number(params.gameId)

  const { data: game } = useQuery(
    queryKeys.rpGames.byId(gameId),
    async () => {
      return Prp2boxApiGames.getById(gameId)
    },
    {
      staleTime: 1000 * 60 * 5, // 5 minutes
      onError: (err) => toast.error(`${err}`),
    }
  )

  const queryParams = {
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
  }
  const {
    data: sessions,
    isLoading,
    isError,
    isFetching,
  } = useQuery(
    queryKeys.rpGames.sessionsById(gameId, queryParams),
    async () => {
      return Prp2boxApiGames.getSessionsById(gameId, queryParams)
    },
    {
      refetchOnWindowFocus: false,
      onError: (err) => toast.error(`${err}`),
      keepPreviousData: true,
      enabled: !!gameId && Boolean(game?.id),
    }
  )

  const table = useMaterialReactTable({
    ...mrtLocalization,
    enableGlobalFilter: false,
    data: sessions?.rows || [],
    rowCount: sessions?.totalRows || 0,
    columns,
    state: {
      pagination,
      isLoading,
      showAlertBanner: isError,
      showProgressBars: isFetching,
    },
    manualPagination: true,
    onPaginationChange: setPagination,
    renderDetailPanel: ({ row }) => {
      const events = row.original.events || []

      if (events.length === 0) {
        return (
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Нет событий для отображения
            </Typography>
          </Paper>
        )
      }

      const getEventIcon = (action: string) => {
        switch (action) {
          case 'newSession':
            return <PlayIcon color="success" />
          case 'changeStatus':
          case 'changePhase':
            return <EventIcon color="info" />
          case 'cardReceived':
          case 'cardBuyGameReceived':
            return <EventIcon color="primary" />
          case 'rollbackStatus':
            return <StopIcon color="warning" />
          case 'cardsReset':
            return <StopIcon color="error" />
          default:
            return <EventIcon color="primary" />
        }
      }

      const getEventColor = (action: string) => {
        switch (action) {
          case 'newSession':
            return 'success'
          case 'changeStatus':
          case 'changePhase':
            return 'info'
          case 'cardReceived':
          case 'cardBuyGameReceived':
            return 'primary'
          case 'rollbackStatus':
            return 'warning'
          case 'cardsReset':
            return 'error'
          default:
            return 'default'
        }
      }

      const getActionLabel = (action: string) => {
        switch (action) {
          case 'newSession':
            return 'Новая сессия'
          case 'changeStatus':
            return 'Смена статуса'
          case 'changePhase':
            return 'Смена фазы'
          case 'cardReceived':
            return 'Карта получена'
          case 'cardBuyGameReceived':
            return 'Карта докупки получена'
          case 'rollbackStatus':
            return 'Откат статуса'
          case 'boxNextTurn':
            return 'Следующий ход бокса'
          case 'cardsReset':
            return 'Сброс карт'
          case 'pendingBuyGameCard':
            return 'Ожидание карты докупки'
          default:
            return action || 'Неизвестное действие'
        }
      }

      return (
        <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            События сессии ({events.length})
          </Typography>
          <List dense>
            {events.map((event, index) => (
              <div key={`${event.action}-${event.timestamp}-${Math.random()}`}>
                <ListItem>
                  <ListItemIcon>{getEventIcon(event.action)}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body1" fontWeight="medium">
                          {getActionLabel(event.action)}
                        </Typography>
                        <Chip
                          label={event.action}
                          size="small"
                          color={getEventColor(event.action) as any}
                          variant="outlined"
                        />
                      </Stack>
                    }
                    secondary={
                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          📅 {formatToMoscowTZ(event.timestamp)}
                        </Typography>
                        {event.details && Object.keys(event.details).length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Детали:
                            </Typography>
                            <Box
                              component="pre"
                              sx={{
                                fontSize: '0.75rem',
                                background: '#f5f5f5',
                                p: 1,
                                borderRadius: 1,
                                overflow: 'auto',
                                maxHeight: 100,
                              }}>
                              {JSON.stringify(event.details, null, 2)}
                            </Box>
                          </Box>
                        )}
                      </Stack>
                    }
                  />
                </ListItem>
                {index < events.length - 1 && <Divider />}
              </div>
            ))}
          </List>
        </Paper>
      )
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        event.preventDefault()
        event.stopPropagation()

        const url = routes.prpSessionDetails
          .replace(':gameId', String(row.original.gameId))
          .replace(':sessionId', String(row.original.id))

        window.open(url, '_blank')
      },
      sx: {
        cursor: 'pointer',
      },
    }),
  })

  if (!game) {
    return <Typography variant="h6">Игра не найдена</Typography>
  }

  return (
    <div>
      <Box>
        <FilterBar>
          <Typography variant="h6">Список сессий: {game.name} </Typography>
        </FilterBar>
      </Box>

      <MaterialReactTable table={table} />
    </div>
  )
})

export default RpGameSessions
